import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { FaSignOutAlt, FaUser, FaBars, FaTimes } from 'react-icons/fa';
import Sidebar from './Sidebar';
import { logout } from '../store/slices/authSlice';
import { resetState as resetMasterData } from '../store/slices/masterDataSlice';
import { resetState as resetDashboard } from '../store/slices/dashboardSlice';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 280px;
  background: #f8f9fa;
  transition: margin-left 0.3s ease;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const TopBar = styled.div`
  background: white;
  padding: 15px 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  
  @media (max-width: 768px) {
    padding: 15px 20px;
  }
`;

const MobileNavButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #2c3e50;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2c3e50;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgb(51, 66, 147);;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const UserName = styled.span`
  font-weight: 500;
  color: #2c3e50;
`;

function getUserDisplayName(user) {
  if (!user) return '';
  const first = user.firstName ?? user.first_name;
  const last = user.lastName ?? user.last_name;
  const full = [first, last].filter((p) => p && String(p).trim()).join(' ').trim();
  if (full) return full;
  const id = user.loginID ?? user.login_id;
  if (id) return String(id);
  return 'User';
}

const LogoutButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: background 0.3s ease;

  &:hover {
    background: #c0392b;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 0.8rem;
  }
`;

const ContentArea = styled.div`
  min-height: calc(100vh - 70px);
  padding: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'block' : 'none'};
  }
`;

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(resetMasterData());
    dispatch(resetDashboard());
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <LayoutContainer>
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      <Overlay isOpen={sidebarOpen} onClick={closeSidebar} />
      <MainContent>
        <TopBar>
          <MobileNavButton onClick={toggleSidebar}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </MobileNavButton>
          <div></div>
          <UserSection style={{ marginRight: '25px' }}>
            <UserInfo>
              <UserAvatar>
                <FaUser />
              </UserAvatar>
              <UserName>{getUserDisplayName(user)}</UserName>
            </UserInfo>
            <LogoutButton onClick={handleLogout}>
              <FaSignOutAlt />
              Logout
            </LogoutButton>
          </UserSection>
        </TopBar>
        <ContentArea>
          {children ?? <Outlet />}
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout; 